import { initializeApp } from "firebase/app";
import { getMessaging, getToken } from "firebase/messaging";
import { getAllDevices } from "./redux/features/devices/devices";
import { updateFcmToken } from "./redux/features/devices/devices";
import { store } from "./redux/store";
import { GlobalConfiguration } from "./utils/configurationProvider";
import {
  getStoredUserNotificationPrefs,
  setStoreUserNotificationPrefs,
} from "./containers/Settings/NotificationCenter";

/**
 * Firebase Messaging instance for receiving foreground push messages.
 * Used by `onMessage()` listeners in the main app (App.js) to display notifications
 * when the page is open and focused.
 *
 * Background pushes (page closed/backgrounded) are handled by the
 * service worker at `/firebase-messaging-sw.js` via its `push` event listener.
 */

const firebaseConfig = {
  /**
   * Firebase configuration loaded from environment variables at build time.
   * These values are injected via Create React App's `process.env` mechanism
   * (REACT_APP_* prefix). See `.env.*` files for per-environment values.
   */
  apiKey: process.env?.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env?.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env?.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env?.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env?.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env?.REACT_APP_FIREBASE_APP_ID,
};

// Initialize the Firebase app instance.
const app = initializeApp(firebaseConfig);

/**
 * Retrieves the first device associated with the current user from the backend.
 * Used to associate the generated FCM token with a specific device record.
 *
 * Dispatches `getAllDevices` with page_size=1 to fetch only the first device.
 *
 */
export const getCurrentDeviceInfo = async () => {
  const response = await store.dispatch(
    getAllDevices({
      page_no: GlobalConfiguration.lengthCheckOne,
      page_size: GlobalConfiguration.lengthCheckOne,
    }),
  );
  if (response?.payload) {
    return response?.payload?.devices?.[GlobalConfiguration?.lengthCheckZero];
  } else {
    return null;
  }
};

/**
 * Removes stale FCM token entries from the user's notification preferences.
 *
 * When a new token is generated (e.g., on a new device or after token rotation),
 * any preferences keyed by the old token become orphaned. This function detects
 * a token change by comparing the newly generated token against the one stored
 * in `localStorage.fcm_token`, and if different, deletes the old key from the
 * preferences object.
 */
const clearOutdatedToken = async (token, userNotificationPreferences) => {
  const previousToken = localStorage.getItem("fcm_token");

  if (previousToken && previousToken !== token) {
    const { [previousToken]: removed, ...rest } = userNotificationPreferences;
    userNotificationPreferences = rest;
  }
  return userNotificationPreferences;
};

/**
 * Generates (or retrieves) an FCM device token for push notifications.
 *
 * Flow:
 * 1. Verifies the Notification API exists and permission is "granted".
 * 2. Fetches the current user's device info (to associate token with device ID).
 * 3. Loads existing notification preferences.
 * 4. Calls Firebase `getToken()` with the VAPID key.
 * 5. Cleans up any stale token entries from preferences via `clearOutdatedToken`.
 * 6. Persists the new token to `localStorage.fcm_token` for change detection on next run.
 * 7. Dispatches `updateFcmToken` to sync the token to the backend (device record).
 * 8. If this is a brand-new token (not in preferences), initializes default
 *    notification preferences (all categories enabled) via `setStoreUserNotificationPrefs`.
 */
export const generateToken = async (serviceWorkerRegistration = null) => {
  if ("Notification" in window) {
    const permission = Notification?.permission;
    if (permission === "granted") {
      const deviceInfo = await getCurrentDeviceInfo();
      let userNotificationPreferences = await getStoredUserNotificationPrefs();
      try {
        const token = await getToken(messaging, {
          vapidKey: process.env?.VAPID_KEY,
          serviceWorkerRegistration,
        });

        // Remove stale token preferences if the token has rotated/changed.
        userNotificationPreferences = await clearOutdatedToken(
          token,
          userNotificationPreferences,
        );

        // Persist token locally for change detection on subsequent runs.
        localStorage.setItem("fcm_token", token);

        // Sync token to backend so the server can target this device for pushes.
        store.dispatch(updateFcmToken({ deviceId: deviceInfo?.id, token }));

        // If this token has no preferences yet, initialize defaults (all enabled).
        if (
          userNotificationPreferences &&
          !userNotificationPreferences.hasOwnProperty(token)
        ) {
          // Enabling notification by default for new user (FCM token)
          await setStoreUserNotificationPrefs({
            ...userNotificationPreferences,
            [token]: {
              global_enabled: true,
              transaction_notification_enabled: true,
              account_notification_enabled: true,
            },
          });
        }
        return token;
      } catch (error) {
        console.log("Error while generating token", error);
      }
    }
  }
};

export const messaging = getMessaging(app);
